import { WS_URL } from '../config';
import { getWebSocketTicket } from './websocketTicketService';

const NON_RETRYABLE_CLOSE_CODES = new Set([
  1000, // Normal closure
  4003, // Auth / access denied
  4004, // Not found
]);

const AUTH_ERROR_CLOSE_CODES = new Set([
  4003, // Auth / access denied
  4004, // Not found
]);

class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity;
    this.baseReconnectInterval = 1000;
    this.maxReconnectInterval = 30000;
    this.listeners = new Map();
    this.isConnecting = false;
    this.currentMerchantId = null;
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;
    this.heartbeatIntervalMs = 30000;
    this.heartbeatTimeoutMs = 10000;
    this.reconnectTimer = null;
    this.intentionalDisconnect = false;

    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._handleVisibilityChange);
    }
  }

  async connect(merchantId) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN && this.currentMerchantId === merchantId) {
      return;
    }

    if (this.isConnecting && this.currentMerchantId === merchantId) {
      return;
    }

    if (this.socket && this.currentMerchantId !== merchantId) {
      this._closeSocket();
    }

    this.intentionalDisconnect = false;
    this.isConnecting = true;
    this.currentMerchantId = merchantId;

    let ticket = null;
    try {
      ticket = await getWebSocketTicket();
    } catch (e) {
      // Ticket fetch failed -- fall back to cookie-only auth
    }

    if (this.intentionalDisconnect) {
      this.isConnecting = false;
      return;
    }

    const baseUrl = `${WS_URL}/ws/merchant/${merchantId}/orders/`;
    const wsUrl = ticket ? `${baseUrl}?ticket=${ticket}` : baseUrl;

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this._startHeartbeat();
        this.notifyListeners('connected', { merchantId });
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'pong') {
            this._onPong();
            return;
          }

          this.handleMessage(data);
        } catch (error) {
          // Invalid message format
        }
      };

      this.socket.onclose = (event) => {
        this.isConnecting = false;
        this._stopHeartbeat();
        this.notifyListeners('disconnected', { code: event.code, reason: event.reason });

        if (this.intentionalDisconnect || !this.currentMerchantId) {
          return;
        }

        if (NON_RETRYABLE_CLOSE_CODES.has(event.code)) {
          if (AUTH_ERROR_CLOSE_CODES.has(event.code)) {
            this.notifyListeners('authError', {
              code: event.code,
              message: event.reason || 'Connection rejected by server',
            });
          }
          return;
        }

        this._scheduleReconnect();
      };

      this.socket.onerror = () => {
        this.isConnecting = false;
        this.notifyListeners('error', {});
      };

    } catch (error) {
      this.isConnecting = false;
      this.notifyListeners('error', { error });
      if (!this.intentionalDisconnect && this.currentMerchantId) {
        this._scheduleReconnect();
      }
    }
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;

    const delay = Math.min(
      this.baseReconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectInterval
    );

    const jitter = delay * 0.2 * Math.random();

    this.reconnectTimer = setTimeout(() => {
      if (this.currentMerchantId && !this.intentionalDisconnect) {
        this.isConnecting = false;
        this.connect(this.currentMerchantId);
      }
    }, delay + jitter);
  }

  _startHeartbeat() {
    this._stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          this.socket.send(JSON.stringify({ type: 'ping' }));
        } catch (e) {
          this._handleDeadConnection();
          return;
        }

        this.heartbeatTimeout = setTimeout(() => {
          this._handleDeadConnection();
        }, this.heartbeatTimeoutMs);
      }
    }, this.heartbeatIntervalMs);
  }

  _onPong() {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  _handleDeadConnection() {
    this._stopHeartbeat();
    if (this.socket) {
      try {
        this.socket.close();
      } catch (e) {
        // Already closed
      }
      this.socket = null;
    }

    if (!this.intentionalDisconnect && this.currentMerchantId) {
      this._scheduleReconnect();
    }
  }

  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  _handleVisibilityChange() {
    if (typeof document === 'undefined') return;

    if (!document.hidden && this.currentMerchantId && !this.intentionalDisconnect) {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        this.reconnectAttempts = 0;
        this.connect(this.currentMerchantId);
      }
    }
  }

  handleMessage(data) {
    switch (data.type) {
      case 'connection_established':
        this.notifyListeners('connected', {
          message: data.message,
          merchant_id: data.merchant_id
        });
        break;
      case 'merchant_order_update':
        this.notifyListeners('orderUpdate', {
          order: data.order,
          action: data.action,
          play_sound: data.play_sound
        });
        break;
      case 'order_update':
        this.notifyListeners('orderUpdate', {
          order: data.order,
          action: 'updated',
          play_sound: data.play_sound
        });
        break;
      case 'order_notification':
        this.notifyListeners('orderUpdate', {
          order: data.order,
          action: data.action,
          play_sound: data.play_sound
        });
        break;
      case 'notification':
        this.notifyListeners('adminNotification', {
          notification: data.notification
        });
        break;
      case 'error':
        this.notifyListeners('serverError', {
          message: data.message,
        });
        break;
      default:
        if (data.order && data.action) {
          this.notifyListeners('orderUpdate', {
            order: data.order,
            action: data.action,
            play_sound: data.play_sound
          });
        }
    }
  }

  _closeSocket() {
    this._stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000, 'Client disconnecting');
      } catch (e) {
        // Already closed
      }
      this.socket = null;
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;
    this._closeSocket();
    this.listeners.clear();
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.currentMerchantId = null;
  }

  addListener(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
  }

  removeListener(eventType, callback) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).delete(callback);
    }
  }

  notifyListeners(eventType, data = {}) {
    if (this.listeners.has(eventType)) {
      const listeners = this.listeners.get(eventType);

      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          // Listener callback failed, continue with others
        }
      });
    }
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  getConnectionState() {
    if (!this.socket) return 'DISCONNECTED';

    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'CONNECTED';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'DISCONNECTED';
      default:
        return 'UNKNOWN';
    }
  }

  onOrderUpdate(callback) {
    this.addListener('orderUpdate', callback);
    return () => this.removeListener('orderUpdate', callback);
  }

  onAdminNotification(callback) {
    this.addListener('adminNotification', callback);
    return () => this.removeListener('adminNotification', callback);
  }

  onConnected(callback) {
    this.addListener('connected', callback);
    return () => this.removeListener('connected', callback);
  }

  onDisconnected(callback) {
    this.addListener('disconnected', callback);
    return () => this.removeListener('disconnected', callback);
  }

  onError(callback) {
    this.addListener('error', callback);
    return () => this.removeListener('error', callback);
  }

  onAuthError(callback) {
    this.addListener('authError', callback);
    return () => this.removeListener('authError', callback);
  }

  onServerError(callback) {
    this.addListener('serverError', callback);
    return () => this.removeListener('serverError', callback);
  }

  offOrderUpdate(callback) {
    this.removeListener('orderUpdate', callback);
  }

  offAdminNotification(callback) {
    this.removeListener('adminNotification', callback);
  }

  offConnected(callback) {
    this.removeListener('connected', callback);
  }

  offDisconnected(callback) {
    this.removeListener('disconnected', callback);
  }

  offError(callback) {
    this.removeListener('error', callback);
  }

  destroy() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    }
    this.disconnect();
  }
}

// Create a singleton instance
const websocketService = new WebSocketService();

export { WebSocketService };
export default websocketService;
